import { FC, useEffect, useMemo, useState, useRef, ChangeEvent } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import styles from "./CreateCustomDashboardModal.module.scss";
import { withError } from "src/hocs";
import { ROUTES, DASHBOARD_INPUT_LIMIT } from "src/constants";
import { removeExtraSpaces, showToastNotification } from "src/utils";
import { useModal, useElementFocus, useTemporaryErrors } from "src/hooks";
import { Form, Input, Label } from "src/components";
import {
  selectUser,
  selectCompanyId,
  selectCompanyDashboards,
} from "src/store/selectors";
import { ConfirmModal } from "../ConfirmModal/ConfirmModal";
import { KeywordsDataSourcesDropdown } from "src/features/Dropdowns/KeywordsDataSourcesDropdown/KeywordsDataSourcesDropdown";
import { LocationsDropdown } from "src/features/Dropdowns/LocationsDropdown/LocationsDropdown";
import { LanguagesDropdown } from "src/features/Dropdowns/LanguagesDropdown/LanguagesDropdown";
import { createCustomDashboard } from "src/store/dashboards/dashboardsApi";

const InputWithError = withError(Input);

type Props = {
  onSubmitHandler?: () => void;
};

export const CreateCustomDashboardModal: FC<Props> = ({ onSubmitHandler }) => {
  const { t } = useTranslation();

  const history = useHistory();

  const { closeModal } = useModal();

  const [ref, setFocus] = useElementFocus();

  const { errors, setErrors } = useTemporaryErrors(3000);

  const dashboards = useSelector(selectCompanyDashboards);

  const user = useSelector(selectUser);

  const companyId = useSelector(selectCompanyId);

  const [title, setTitle] = useState<string>("");
  const [dataSource, setDataSource] =
    useState<Nullable<Search.KeywordsDataSource>>(null);
  const [locationId, setLocationId] =
    useState<Nullable<Location.Data["id"]>>(null);
  const [languageId, setLanguageId] =
    useState<Nullable<Language.Data["id"]>>(null);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvFileName, setCsvFileName] = useState<string>("");

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>("idle");

  const isLoading = useMemo<boolean>(
    () => loadingStatus === "loading",
    [loadingStatus],
  );

  const isFormChanged = useMemo<boolean>(
    () => Boolean(title || dataSource || locationId || languageId || csvFile),
    [title, dataSource, locationId, languageId, csvFile],
  );

  const isDisabled = useMemo<boolean>(
    () => isLoading || !isFormChanged,
    [isLoading, isFormChanged],
  );

  useEffect(() => setFocus(), [setFocus]);

  const onTitleChange = (value: string): void => setTitle(value);

  const onDataSourceChange = (value: Search.KeywordsDataSource): void =>
    setDataSource(value);

  const onLocationIdChange = (value: Location.Data["id"]): void =>
    setLocationId(value);

  const onLanguageIdChange = (value: Language.Data["id"]): void =>
    setLanguageId(value);

  const onFileChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];

    if (!file) {
      setCsvFile(null);
      setCsvFileName("");
      return;
    }

    // Validate file type
    const fileName = file.name.toLowerCase();
    const isValidCsv =
      fileName.endsWith(".csv") ||
      file.type === "text/csv" ||
      file.type === "application/vnd.ms-excel";

    if (!isValidCsv) {
      setErrors({
        ...errors,
        csvFile: t(
          "component.modal.create_custom_dashboard.form.validation.csv_file_invalid",
        ),
      });
      setCsvFile(null);
      setCsvFileName("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setCsvFile(file);
    setCsvFileName(file.name);
    setErrors({
      ...errors,
      csvFile: "",
    });
  };

  const onFileRemove = (): void => {
    setCsvFile(null);
    setCsvFileName("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const onSubmit = async (): Promise<void> => {
    const validationErrors = validate();

    if (Object.keys(validationErrors).length)
      return setErrors(validationErrors);

    if (!companyId || !user?.id || !csvFile) return;

    try {
      setLoadingStatus("loading");

      const result = await createCustomDashboard({
        companyId,
        authorId: user.id,
        dashboardName: removeExtraSpaces(title),
        locationId: locationId!,
        languageId: languageId!,
        keywordsDataSource: dataSource!,
        csvFile,
      });

      setLoadingStatus("succeeded");

      // Navigate to the created dashboard
      if (result.dashboardId) {
        history.push(`${ROUTES.dashboardsHomePage}/${result.dashboardId}`);
      }

      onSubmitHandler?.();

      closeModal("create-custom-dashboard");
    } catch (error) {
      console.error(error);

      showToastNotification({
        type: "error",
        text: t("common.error.server_error"),
      });

      setLoadingStatus("failed");
    }
  };

  function validate() {
    const validationErrors: Record<string, string> = {};

    if (!title.trim().length) {
      validationErrors.title = t(
        "component.modal.create_custom_dashboard.form.validation.title_required",
      );
    }

    if (title.trim().length) {
      const dashboardByName = dashboards.find(
        (dashboard) => dashboard.name.trim() === title.trim(),
      );

      if (dashboardByName) {
        validationErrors.title = t(
          "component.modal.create_custom_dashboard.form.validation.title_exists",
        );
      }
    }

    if (!dataSource) {
      validationErrors.dataSource = t(
        "component.modal.create_custom_dashboard.form.validation.data_source_required",
      );
    }

    if (!locationId) {
      validationErrors.locationId = t(
        "component.modal.create_custom_dashboard.form.validation.location_required",
      );
    }

    if (!languageId) {
      validationErrors.languageId = t(
        "component.modal.create_custom_dashboard.form.validation.language_required",
      );
    }

    if (!csvFile) {
      validationErrors.csvFile = t(
        "component.modal.create_custom_dashboard.form.validation.csv_file_required",
      );
    }

    return validationErrors;
  }

  const keywordsDataSources = useMemo<Nullable<Search.KeywordsDataSource>[]>(
    () => (dataSource ? [dataSource] : []),
    [dataSource],
  );

  return (
    <ConfirmModal
      id="create-custom-dashboard"
      type="success"
      acceptButton={{
        onClick: onSubmit,
        text: t("component.modal.create_custom_dashboard.button.submit"),
        disabled: isDisabled,
      }}
      cancelButton={{
        onClick: () => closeModal("create-custom-dashboard"),
        text: t("component.modal.create_custom_dashboard.button.cancel"),
      }}
      title={t("component.modal.create_custom_dashboard.title")}
      isLoading={isLoading}
    >
      <Form
        onSubmit={onSubmit}
        disabled={isDisabled}
        className={styles.formWrapper}
        encType={"multipart/form-data"}
      >
        <div className={styles.inputWrapper}>
          <Label
            leftText={t(
              "component.modal.create_custom_dashboard.form.label.title",
            )}
          />
          <InputWithError
            ref={ref}
            value={title}
            error={errors.title}
            changeHandler={onTitleChange}
            characterLimit={DASHBOARD_INPUT_LIMIT}
            placeholder={t(
              "component.modal.create_custom_dashboard.form.placeholder.title",
            )}
          />
        </div>
        <div className={styles.inputWrapper}>
          <Label
            leftText={t(
              "component.modal.create_custom_dashboard.form.label.data_source",
            )}
          />
          <div className={styles.dropdownWrapper}>
            <KeywordsDataSourcesDropdown
              dataSource={dataSource}
              setDataSource={onDataSourceChange}
              error={errors.dataSource}
              openingDirection="bottom-end"
            />
          </div>
        </div>
        <div className={styles.groupWrapper}>
          <div className={styles.inputWrapper}>
            <Label
              leftText={t(
                "component.modal.create_custom_dashboard.form.label.region",
              )}
            />
            <div className={styles.dropdownWrapper}>
              <LocationsDropdown
                locationId={locationId}
                setLocationId={onLocationIdChange}
                keywordsDataSources={keywordsDataSources}
                error={errors.locationId}
                placeholder={t(
                  "component.modal.create_custom_dashboard.form.placeholder.region",
                )}
              />
            </div>
          </div>

          <div className={styles.inputWrapper}>
            <Label
              leftText={t(
                "component.modal.create_custom_dashboard.form.label.language",
              )}
            />
            <div className={styles.dropdownWrapper}>
              <LanguagesDropdown
                languageId={languageId}
                setLanguageId={onLanguageIdChange}
                keywordsDataSources={keywordsDataSources}
                error={errors.languageId}
                placeholder={t(
                  "component.modal.create_custom_dashboard.form.placeholder.language",
                )}
              />
            </div>
          </div>
        </div>
        <div className={styles.inputWrapper}>
          <Label
            leftText={t(
              "component.modal.create_custom_dashboard.form.label.csv_file",
            )}
          />
          <div className={styles.fileInputWrapper}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={onFileChange}
              className={styles.fileInput}
              id="csv-file-input"
            />
            <label htmlFor="csv-file-input" className={styles.fileInputLabel}>
              {csvFileName ||
                t(
                  "component.modal.create_custom_dashboard.form.placeholder.csv_file",
                )}
            </label>
            {csvFileName && (
              <button
                type="button"
                onClick={onFileRemove}
                className={styles.fileRemoveButton}
              >
                ×
              </button>
            )}
            {errors.csvFile && (
              <div className={styles.error}>
                <span>{errors.csvFile}</span>
              </div>
            )}
          </div>
        </div>
      </Form>
    </ConfirmModal>
  );
};
